import { mutate } from "swr";
import { Agent, PartialAgent } from "~/services/agents";
import { apiMutate } from "~/utils/apiClient";

// Project CRUD and attachments live on the Projects service, reached directly
// through the `/api/projects` prefix. Two paths are exceptions: the "start a
// conversation" mutation flows through the analysis-agent (it derives `app_id`
// from the bound agent), and the project skills read is still served by the
// conversations service (the Projects service owns no skills) — see
// `projectSkillsPath` below.
const { VITE_ANALYSIS_AGENT_API, VITE_CONVERSATIONS_API, VITE_PROJECTS_API } = import.meta.env;
export const PROJECTS_API_PREFIX = `${VITE_PROJECTS_API}/projects`;
const CONVERSATIONS_PROJECTS_PREFIX = `${VITE_CONVERSATIONS_API}/projects`;
const ANALYSIS_AGENT_PROJECTS_PREFIX = `${VITE_ANALYSIS_AGENT_API}/projects`;

/** Which slice of projects the list endpoint returns. `usable` is everything the
 *  caller can use; `owned` narrows to those they manage; `all` is the admin
 *  visibility bypass. The UI only offers the views a caller's permissions allow. */
export const ProjectListView = {
  USABLE: "usable",
  OWNED: "owned",
  ALL: "all",
} as const;
export type ProjectListView = (typeof ProjectListView)[keyof typeof ProjectListView];

export const projectListPath = (params?: {
  limit?: number;
  offset?: number;
  view?: ProjectListView;
}) => {
  const query = new URLSearchParams();
  if (params?.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params?.offset !== undefined) {
    query.set("offset", String(params.offset));
  }
  // The default (`usable`) is omitted so it matches the backend default and
  // shares its cache key with a view-less call.
  if (params?.view && params.view !== ProjectListView.USABLE) {
    query.set("view", params.view);
  }
  const qs = query.toString();
  return qs ? `${PROJECTS_API_PREFIX}?${qs}` : PROJECTS_API_PREFIX;
};

/** Revalidate every cached projects-list key, including the `?view=` variants
 *  (`owned`, `all`). A bare `mutate(projectListPath())` only hits the default
 *  key and would leave a non-default view stale after a create/delete/leave.
 *  Matches the list endpoint and its query variants, not per-project reads. */
export const mutateProjectList = () =>
  mutate(
    (key) =>
      typeof key === "string" &&
      (key === PROJECTS_API_PREFIX || key.startsWith(`${PROJECTS_API_PREFIX}?`))
  );

export const projectPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}`;

export const projectAgentsPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/agents`;

export const projectDatasetsPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/datasets`;

// The skills a conversation in this project would receive: the
// conversations-service resolves the project's attached datasets (dropping any
// the caller can't read), and returns the deduped USER + AGENT + DATASET union
// (no SYSTEM) — the same set the in-conversation picker gets. `agentId` folds
// in that agent's AGENT-scope skills, matching the conversation-scoped path.
// Skills live on the conversations service, not the Projects service, so this
// path keeps the conversations prefix while the rest of the project API is
// served directly by the Projects service.
export const projectSkillsPath = (id: string, agentId?: string | null) =>
  `${CONVERSATIONS_PROJECTS_PREFIX}/${id}/skills${agentId ? `?agent_id=${agentId}` : ""}`;

export const projectConversationsPath = (id: string) =>
  `${PROJECTS_API_PREFIX}/${id}/conversations`;

export const projectLeavePath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/leave`;

export const projectSharesPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/shares`;

export const projectSharesBatchPath = () => `${PROJECTS_API_PREFIX}/shares`;

// `POST /projects/{id}/conversations` still hits the analysis-agent so it can
// look up the agent and stamp `app_id` onto the new conversation row.
const projectConversationsCreatePath = (id: string) =>
  `${ANALYSIS_AGENT_PROJECTS_PREFIX}/${id}/conversations`;

export interface PartialProject {
  id: string;
  short_id: string;
  owner_user_id: string;
  name: string;
  description: string | null;
  accent_color: string | null;
  created_at: string;
  updated_at: string;
  // The caller's own access to this project, resolved per-request against
  // OpenFGA. Drives gating of owner-only controls in the UI.
  access_level: ProjectAccessLevel;
}

export type Project = PartialProject;

export interface ProjectListItem extends PartialProject {
  agents: PartialAgent[];
  dataset_count: number;
  // Greater of the project's creation time and the caller's newest
  // conversation update in it.
  last_activity_at: string;
}

export interface ProjectListMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface ProjectList {
  items: ProjectListItem[];
  metadata: ProjectListMetadata;
}

export interface CreateProjectInput {
  name: string;
  description?: string | null;
}

export interface UpdateProjectInput {
  name?: string;
  description?: string | null;
  accent_color?: string | null;
}

export async function createProject(input: CreateProjectInput): Promise<Project> {
  return apiMutate<Project>(PROJECTS_API_PREFIX, "POST", input);
}

export async function updateProject(id: string, input: UpdateProjectInput): Promise<Project> {
  return apiMutate<Project>(projectPath(id), "PATCH", input);
}

export async function deleteProject(id: string): Promise<Project> {
  return apiMutate<Project>(projectPath(id), "DELETE");
}

// Remove the caller's own access. The backend returns 204; a 409 means the
// caller is the project's only owner (or access is group-inherited).
export async function leaveProject(id: string): Promise<void> {
  await apiMutate<null>(projectLeavePath(id), "POST", undefined, {
    expects204: true,
  });
}

export async function setProjectAgents(id: string, agent_ids: string[]): Promise<Agent[]> {
  return apiMutate<Agent[]>(projectAgentsPath(id), "PUT", { agent_ids });
}

export async function setProjectDatasets(id: string, dataset_ids: string[]): Promise<string[]> {
  return apiMutate<string[]>(projectDatasetsPath(id), "PUT", { dataset_ids });
}

export interface ProjectConversation {
  id: string;
  user_id: string;
  agent_id: string | null;
  agent: PartialAgent | null;
  project_id: string;
  project_uuid: string;
  display_name: string;
  status: string;
  status_updated_at: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectConversationList {
  items: ProjectConversation[];
  limit: number | null;
  offset: number;
  total: number;
}

export interface CreateProjectConversationInput {
  agent_id: string;
  user_timezone?: string;
}

export async function createProjectConversation(
  projectId: string,
  input: CreateProjectConversationInput
): Promise<ProjectConversation> {
  return apiMutate<ProjectConversation>(projectConversationsCreatePath(projectId), "POST", input);
}

// Project sharing. Backend maps USER -> can_use and OWNER -> can_manage in
// OpenFGA; principals are users (by email) or user-tags (groups, by name).
export const ProjectAccessLevel = {
  USER: "user",
  OWNER: "owner",
} as const;
export type ProjectAccessLevel = (typeof ProjectAccessLevel)[keyof typeof ProjectAccessLevel];

// A user who inherits access through a shared group (user-tag). Populated only
// on the single-project collaborators endpoint, not the batch list.
export interface ProjectCollaboratorMember {
  email: string;
  given_name: string | null;
  family_name: string | null;
  image_url: string | null;
}

export interface ProjectCollaborator {
  /** Bare prefixed principal: `user:{email}` or `user-tag:{tag}`. */
  principal: string;
  level: ProjectAccessLevel;
  // Profile fields, populated for user principals; null for groups.
  given_name: string | null;
  family_name: string | null;
  image_url: string | null;
  // Group (user-tag) fields, populated for user-tag principals; absent/empty for users.
  display_name?: string | null;
  is_internal?: boolean;
  members?: ProjectCollaboratorMember[];
}

export interface ProjectCollaboratorList {
  items: ProjectCollaborator[];
}

/** Share a project with a principal at the given level. Idempotent: re-sharing
 * an existing `(principal, level)` succeeds. */
export async function shareProject(
  id: string,
  principal: string,
  level: ProjectAccessLevel
): Promise<ProjectCollaborator> {
  return apiMutate<ProjectCollaborator>(projectSharesPath(id), "POST", {
    principal,
    level,
  });
}

/** Unshare a project from a principal. Idempotent: unsharing an absent share
 * still succeeds. */
export async function unshareProject(
  id: string,
  principal: string,
  level: ProjectAccessLevel
): Promise<void> {
  await apiMutate(projectSharesPath(id), "DELETE", { principal, level }, { expects204: true });
}

// Collaborators for a page of projects, keyed by project id. Fetched in a second
// phase after the list renders so collaborator avatars don't block (or break)
// the list. Projects the caller can't use are omitted from the mapping.
export interface ProjectCollaboratorsBatch {
  collaborators_by_project: Record<string, ProjectCollaborator[]>;
}

export async function fetchProjectCollaboratorsBatch(
  projectIds: string[]
): Promise<ProjectCollaboratorsBatch> {
  return apiMutate<ProjectCollaboratorsBatch>(projectSharesBatchPath(), "POST", {
    project_ids: projectIds,
  });
}
